import React from 'react';
import styled from 'styled-components';
import { Body5, Container, H5 } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { useVaultDocuments } from '@hooks/features/useVaultDocuments';
import { useVaultFilterUrlSync } from '@hooks/features/useVaultFilterUrlSync';
import { VAULT_VIEW_MODES, getDocumentStatus } from '@utils/documentVault';
import { DocumentVaultFilterContext } from '@/contexts/DocumentVaultFilterContext';
import { VaultFilterBar } from './filters/VaultFilterBar';
import { VaultFilterChips } from './filters/VaultFilterChips';
import { VaultViewControls } from './VaultViewControls';
import { VaultTable } from './table/VaultTable';
import { VaultLoadingRows } from './table/VaultLoadingRows';
import { LoadFailedState, NoDocumentsState, NoMatchesState } from './table/VaultEmptyStates';
import { VaultTreeView } from './tree/VaultTreeView';
import { DocumentDetailDrawer } from './detail/DocumentDetailDrawer';
import { DocumentVaultSelectionProvider } from '@/contexts/DocumentVaultSelectionContext';
import { DownloadQueueProvider } from '@/contexts/DownloadQueueContext';
import { SelectAllMatchingBanner } from './selection/SelectAllMatchingBanner';
import { SelectionActionBar } from './selection/SelectionActionBar';
import { DownloadQueuePanel } from './downloads/DownloadQueuePanel';
import { RefreshNewDocsButton } from './management/RefreshNewDocsButton';
import { useDocumentMutations } from '@hooks/features/useDocumentMutations';

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const HeaderControlsWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  button {
    padding: 6px 12px;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const Pagination = ({ page, pageCount }) => {
  const { setPage } = React.useContext(DocumentVaultFilterContext);

  if (pageCount <= 1) return null;

  return (
    <Container top={24}>
      <PaginationWrapper>
        {/* `disabled` on the shared buttons only styles them, so the page guard stays in the handler. */}
        <SkeletonButton disabled={page <= 1} onClick={() => page > 1 && setPage(page - 1)}>
          Previous
        </SkeletonButton>
        <Body5 isLowContrast>
          Page {page} of {pageCount}
        </Body5>
        <SkeletonButton disabled={page >= pageCount} onClick={() => page < pageCount && setPage(page + 1)}>
          Next
        </SkeletonButton>
      </PaginationWrapper>
    </Container>
  );
};

/*
  Selection and the download queue both need the filtered document set, which only exists after
  the fetch -- so the exported screen is a thin data wrapper and the UI reads the providers.
*/
export function DocumentVaultScreen() {
  const vault = useVaultDocuments();

  useVaultFilterUrlSync();

  return (
    <DocumentVaultSelectionProvider filteredDocs={vault.filteredDocs}>
      <DownloadQueueProvider>
        <VaultScreenContent {...vault} />
      </DownloadQueueProvider>
    </DocumentVaultSelectionProvider>
  );
}

function VaultScreenContent({
  docs,
  filteredDocs,
  allDocs,
  facetOptions,
  statusLookup,
  totalCount,
  filteredCount,
  page,
  pageCount,
  isLoading,
  hasError,
  newlyProcessedCount,
  onRefreshDocs,
}) {
  const { filters, clearAll } = React.useContext(DocumentVaultFilterContext);
  const [detailDoc, setDetailDoc] = React.useState(null);
  // Renaming or retagging can move a document between groups, so the list is refetched rather
  // than patched in place.
  const mutations = useDocumentMutations({ onAfterChange: onRefreshDocs });

  /*
    The drawer holds an id, not the document it opened with: a rename or a retag refetches the
    list, and the snapshot it was handed would keep showing the values it was opened with. Looked
    up against the whole library rather than the filtered set, since a new tag can push a document
    out of the current filters while its drawer is still open.
  */
  const activeDetailDoc = React.useMemo(() => {
    if (!detailDoc) return null;
    return (allDocs || []).find((doc) => doc.id === detailDoc.id) || detailDoc;
  }, [detailDoc, allDocs]);

  // Retrying a failed bulk download needs the documents back from their ids.
  const docsById = React.useMemo(() => {
    const lookup = {};
    (filteredDocs || []).forEach((doc) => {
      lookup[doc.id] = doc;
    });
    return lookup;
  }, [filteredDocs]);

  const isTreeView = filters.viewMode === VAULT_VIEW_MODES.tree;
  // The tree groups the whole filtered set: paging inside collapsible folders would hide
  // matches behind two layers at once.
  const visibleDocs = isTreeView ? filteredDocs : docs;

  const renderBody = () => {
    if (isLoading) return <VaultLoadingRows density={filters.density} />;
    if (hasError) return <LoadFailedState onRetry={onRefreshDocs} />;
    if (totalCount === 0) return <NoDocumentsState />;
    if (filteredCount === 0) return <NoMatchesState onClearFilters={clearAll} />;

    if (isTreeView) {
      return (
        <VaultTreeView
          docs={visibleDocs}
          statusLookup={statusLookup}
          density={filters.density}
          onShowDetails={setDetailDoc}
        />
      );
    }

    return (
      <>
        <VaultTable
          docs={visibleDocs}
          statusLookup={statusLookup}
          density={filters.density}
          onShowDetails={setDetailDoc}
        />
        <Pagination page={page} pageCount={pageCount} />
      </>
    );
  };

  return (
    <Container bottom={24}>
      {/*
        Fixed to the top of the viewport and out of the flow, so it never reflows the page --
        while a selection is active it covers the screen header the way it covers the table.
        Rendered first so reading order matches what is on screen.
      */}
      <SelectionActionBar />

      <Container top={16} bottom={16}>
        <HeaderWrapper>
          <H5>Document Vault</H5>
          <HeaderControlsWrapper>
            <VaultViewControls />
          </HeaderControlsWrapper>
        </HeaderWrapper>
      </Container>

      <VaultFilterBar facetOptions={facetOptions} />
      <VaultFilterChips />

      <Container top={16} bottom={8}>
        <Body5 isLowContrast>
          {isLoading
            ? 'Loading documents…'
            : `${filteredCount} ${filteredCount === 1 ? 'document' : 'documents'}${
                filteredCount !== totalCount ? ` of ${totalCount}` : ''
              }`}
        </Body5>
      </Container>

      {!isLoading && !hasError ? (
        <RefreshNewDocsButton count={newlyProcessedCount} onRefreshDocs={onRefreshDocs} />
      ) : null}

      {!isLoading && !hasError && filteredCount > 0 ? (
        <Container bottom={8}>
          <SelectAllMatchingBanner pageCount={docs.length} filteredCount={filteredCount} />
        </Container>
      ) : null}

      {renderBody()}

      <DownloadQueuePanel docsById={docsById} />

      {activeDetailDoc ? (
        <DocumentDetailDrawer
          doc={activeDetailDoc}
          status={getDocumentStatus(activeDetailDoc, statusLookup)}
          onClose={() => setDetailDoc(null)}
          mutations={mutations}
        />
      ) : null}
    </Container>
  );
}
